import { uniqBy } from "lodash";
import { isNotNull } from "src/services/predicates";
import * as GQL from "src/types/graphql";

export type OrderedItem = {
  id: string;
  label: string;
  order: number;
  binId?: string | null;
  gradeId?: string;
};

export type GradeProgram = {
  grade: OrderedItem;
  program: OrderedItem | null;
};

export type GradeProgramWithID = GradeProgram & {
  id: string;
};

export type GradesGroup = {
  id: string;
  lists: GradesGroupLists;
  gradePrograms: GradeProgramWithID[];
};

export type GradesGroupLists = {
  grades: OrderedItem[];
  programs: OrderedItem[];
};

export function gradesGroupToLists(
  gradesGroup: GQL.GetGradesGroupsByEnrollmentSchool_grades_group
): GradesGroupLists {
  // Check that enrollment period and school are consistent between grades group and program groups in it.
  if (
    !gradesGroup.grade_programs.every(
      (gradeProgram) =>
        gradesGroup.enrollment_period_id ===
          gradeProgram.enrollment_period_id &&
        gradesGroup.school_id === gradeProgram.school_id
    )
  ) {
    console.error(
      `Grades group id ${gradesGroup.id} includes grade program(s) from different enrollment periods and/or schools.`
    );
  }

  const gradePrograms = gradesGroupToGradePrograms(gradesGroup);

  const { grades, programs } = gradeProgramsToLists(gradePrograms);

  // The number of grade programs should be equal to number of grades * number of programs.
  if (grades.length * programs.length !== gradesGroup.grade_programs.length) {
    console.error(
      "Grades group is not a valid cross product of grades and programs"
    );
  }

  return { grades, programs };
}

export function gradesGroupToGradePrograms(
  gradesGroup: GQL.GetGradesGroupsByEnrollmentSchool_grades_group
): GradeProgramWithID[] {
  return gradesGroup.grade_programs.map(
    (gradeProgram) =>
      ({
        id: gradeProgram.id,
        grade: {
          id: gradeProgram.grade_config.id,
          label: gradeProgram.grade_config.label,
          order: gradeProgram.grade_config.order,
          binId: gradeProgram.bin_id ?? undefined,
        },
        program: gradeProgram.program,
      } satisfies GradeProgramWithID)
  );
}

function gradeProgramsToLists(gradePrograms: GradeProgram[]): GradesGroupLists {
  const grades = uniqBy(
    gradePrograms.map((gradeProgram) => gradeProgram.grade),
    "id"
  );
  const programs = uniqBy(
    gradePrograms.map((gradeProgram) => gradeProgram.program),
    "id"
  ).filter(isNotNull);

  return { grades, programs };
}

export function listsToGradesGroupInsert({
  lists,
  enrollmentPeriodId,
  schoolId,
  schoolDefaultBinConfigId,
}: {
  lists: GradesGroupLists;
  enrollmentPeriodId: string;
  schoolId: string;
  schoolDefaultBinConfigId: string | null;
}): GQL.grades_group_insert_input {
  const gradePrograms = listsToGradePrograms(lists);
  const formattedGradePrograms = formatGradeProgramsToGradesGroupInsert({
    gradePrograms,
    enrollmentPeriodId,
    schoolId,
    schoolDefaultBinConfigId,
  });
  return {
    enrollment_period_id: enrollmentPeriodId,
    school_id: schoolId,
    grade_programs: {
      data: formattedGradePrograms,
      on_conflict: {
        constraint:
          GQL.grade_constraint
            .grade_enrollment_period_id_grade_config_id_school_id_program_id,
        update_columns: [GQL.grade_update_column.grades_group_id],
      },
    },
  };
}

export function listsToGradePrograms({
  grades,
  programs,
}: GradesGroupLists): GradeProgram[] {
  return grades.flatMap((grade) =>
    programs.map((program) => ({
      grade: grade,
      program: program,
    }))
  );
}

function formatGradeProgramsToGradesGroupInsert({
  gradePrograms,
  enrollmentPeriodId,
  schoolId,
  schoolDefaultBinConfigId,
}: {
  gradePrograms: GradeProgram[];
  enrollmentPeriodId: string;
  schoolId: string;
  schoolDefaultBinConfigId: string | null;
}): GQL.grade_insert_input[] {
  return gradePrograms.map((gradeProgram) => ({
    enrollment_period_id: enrollmentPeriodId,
    school_id: schoolId,
    grade_config_id: gradeProgram.grade.id,
    program_id: gradeProgram.program?.id,
    bin: {
      data: {
        enrollment_period_id: enrollmentPeriodId,
        bin_config_id: schoolDefaultBinConfigId,
      },
    },
  }));
}

export function orderedItemSortFn(a: OrderedItem, b: OrderedItem) {
  return a.order - b.order;
}
